// Storage service using n8n + Cloudflare R2
// Replaces Firebase for image storage

// DEBUG: Log environment variable loading
console.log('[storage.js] Environment REACT_APP_N8N_SAVE_IMAGE_URL:', process.env.REACT_APP_N8N_SAVE_IMAGE_URL);

const N8N_SAVE_IMAGE_URL = process.env.REACT_APP_N8N_SAVE_IMAGE_URL || 'https://n8n.dleaf.net/webhook/save-image';

console.log('[storage.js] Using URL:', N8N_SAVE_IMAGE_URL);
console.log('[storage.js] Source:', process.env.REACT_APP_N8N_SAVE_IMAGE_URL ? 'environment' : 'fallback');

/**
 * Clean base64 data by removing data URL prefix
 * @param {string} base64Data - Base64 data URL
 * @returns {string} Clean base64 string
 */
const cleanBase64 = (base64Data) => {
  if (!base64Data) {
    console.error('[cleanBase64] ERROR: null/undefined data');
    return '';
  }
  const cleaned = base64Data.split(',')[1] || base64Data;
  console.log('[cleanBase64]', base64Data.length, '->', cleaned.length);
  return cleaned;
};

/**
 * Save generated image to R2 via n8n
 * @param {string} imageData - Base64 encoded image data
 * @param {string} styleId - The style used for generation
 * @returns {Promise<Object>} Object containing imageId and imageUrl
 */
export const saveGeneratedImage = async (imageData, styleId) => {
  try {
    console.log('========== IMAGE SAVE START ==========');
    console.log('[SAVE] URL:', N8N_SAVE_IMAGE_URL);
    console.log('[SAVE] Image data:', imageData ? 'present' : 'MISSING');
    console.log('[SAVE] Image data length:', imageData?.length || 0);
    console.log('[SAVE] Style ID:', styleId);

    const cleanedData = cleanBase64(imageData);
    const payload = {
      imageData: cleanedData,
      styleId,
      timestamp: new Date().toISOString()
    };

    const payloadSizeMB = (cleanedData.length / (1024 * 1024)).toFixed(2);
    console.log('[SAVE] Payload keys:', Object.keys(payload));
    console.log('[SAVE] Payload imageData length:', cleanedData.length, `(${payloadSizeMB} MB)`);

    // Warn if payload is too large
    if (cleanedData.length > 10 * 1024 * 1024) {
      console.warn('[SAVE] WARNING: Payload exceeds 10MB - may timeout or fail');
    }

    console.log('[SAVE] Sending POST request...');

    let response;
    try {
      // Increase timeout to 90 seconds for large uploads and QR generation
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error('[SAVE] Request exceeded 90 second timeout');
        controller.abort();
      }, 90000);

      const startTime = Date.now();

      response = await fetch(N8N_SAVE_IMAGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const duration = ((Date.now() - startTime) / 1000).toFixed(2);
      console.log('[SAVE] Response received');
      console.log('[SAVE] Response status:', response.status);
      console.log('[SAVE] Request duration:', duration, 'seconds');
      console.log('[SAVE] Response headers:', Object.fromEntries(response.headers.entries()));
    } catch (fetchError) {
      console.error('[SAVE] FETCH FAILED:', fetchError.message);
      if (fetchError.name === 'AbortError') {
        console.error('[SAVE] Request timed out after 90 seconds - payload may be too large');
      } else {
        console.error('[SAVE] Network error - check CORS and n8n webhook availability');
      }
      throw fetchError;
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[SAVE] ERROR Response Status:', response.status, response.statusText);
      console.error('[SAVE] ERROR Response Body:', errorText);
      throw new Error(`Failed to save image: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // Log raw response before parsing
    const responseText = await response.text();
    console.log('[SAVE] Raw response:', responseText.substring(0, 500)); // First 500 chars

    let result;
    try {
      result = JSON.parse(responseText);
    } catch (jsonError) {
      console.error('[SAVE] JSON PARSE FAILED');
      console.error('[SAVE] Raw text:', responseText);
      throw new Error('Storage service returned invalid JSON response');
    }

    console.log('========== IMAGE SAVE SUCCESS ==========');
    console.log('[SAVE] Full response:', JSON.stringify(result, null, 2));
    console.log('[SAVE] Image ID:', result.imageId);
    console.log('[SAVE] Image URL:', result.imageUrl);

    // Validate response structure
    if (!result.imageId || !result.imageUrl) {
      console.error('[SAVE] VALIDATION FAILED - Missing required fields');
      console.error('[SAVE] Received:', result);
      throw new Error(
        `Invalid response from storage service: ${
          !result.imageId ? 'missing imageId' : 'missing imageUrl'
        }`
      );
    }

    // Additional validation: check if imageUrl is a valid URL
    try {
      new URL(result.imageUrl);
    } catch (urlError) {
      console.error('[SAVE] VALIDATION FAILED - Invalid URL:', result.imageUrl);
      throw new Error(`Invalid image URL received: ${result.imageUrl}`);
    }

    // Return both imageId and imageUrl from n8n response
    return {
      imageId: result.imageId,
      imageUrl: result.imageUrl
    };
  } catch (error) {
    console.error('========== IMAGE SAVE FAILED ==========');
    console.error('[SAVE ERROR] Name:', error.name);
    console.error('[SAVE ERROR] Message:', error.message);
    console.error('[SAVE ERROR] Stack:', error.stack);

    // Preserve specific error messages for better debugging
    if (error.name === 'AbortError') {
      throw new Error('Upload timed out after 90 seconds. The image may be too large or the network is slow. Please try again.');
    }

    if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
      throw new Error('Unable to connect to storage service. Please check your internet connection and try again.');
    }

    if (error.message.includes('Invalid response') || error.message.includes('missing')) {
      throw new Error('The storage service returned incomplete data. Please try again or contact support if the issue persists.');
    }

    // Re-throw with original message if it's already descriptive
    if (error.message.includes('Invalid image URL') ||
        error.message.includes('missing imageId') ||
        error.message.includes('invalid JSON')) {
      throw error;
    }

    throw new Error(`Failed to save image: ${error.message}. Please try again.`);
  }
};

/**
 * Generate shareable URL for the image
 * Since images are stored in public R2 bucket, we can directly use the R2 URL
 * @param {string} imageId - Image ID from save operation
 * @returns {string} Direct R2 URL to the image
 */
export const generateImageUrl = (imageId) => {
  // R2 public bucket URL - update this with your actual R2 public bucket URL
  const R2_PUBLIC_URL = process.env.REACT_APP_R2_PUBLIC_URL || 'https://pub-xxxxx.r2.dev';
  return `${R2_PUBLIC_URL}/${imageId}.jpg`;
};

/**
 * Get image data (not needed for R2 since images are publicly accessible)
 * This function exists for compatibility with the original Firebase implementation
 * @param {string} imageId - Image ID
 * @returns {Promise<Object>} Image data
 */
export const getGeneratedImage = async (imageId) => {
  // For R2, images are public and accessible directly via URL
  // No need to fetch from Firestore
  const imageUrl = generateImageUrl(imageId);

  return {
    id: imageId,
    imageData: imageUrl, // Return URL instead of base64
    isUrl: true // Flag to indicate this is a URL, not base64
  };
};
